// frontend/src/components/ReportChatButton.jsx
import { useState } from "react";
import { createPortal } from "react-dom"; // 🔥 NUEVO
import { Flag, Loader2, AlertTriangle, X, ChevronRight } from "lucide-react";
import Swal from "sweetalert2";
import api from "../config/api";
import { useAuth } from "../context/AuthContext";

const REPORT_REASONS = [
    { value: "Conteúdo ofensivo ou discriminatório", label: "Conteúdo ofensivo" },
    { value: "Assédio ou intimidação", label: "Assédio ou intimidação" },
    { value: "Spam ou mensagens indesejadas", label: "Spam" },
    { value: "Comportamento inadequado", label: "Comportamento inadequado" },
    { value: "Fake news ou informação falsa", label: "Informação falsa" },
    { value: "Golpe ou fraude", label: "Golpe ou fraude" },
    { value: "Vendedor não confiável", label: "Vendedor não confiável" },
    { value: "Outro motivo", label: "Outro motivo" }
];

export default function ReportChatButton({ conversationId, otherUserName, postTitle }) {
    const { user } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedReason, setSelectedReason] = useState("");
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);

    const handleOpenModal = (e) => {
        e.stopPropagation();
        e.preventDefault();
        
        if (!user) {
            Swal.fire({
                icon: "info",
                title: "Faça login",
                text: "Você precisa estar logado para denunciar uma conversa.",
                confirmButtonColor: "#449879",
            });
            return;
        }

        if (user.status !== "aprovado") {
            Swal.fire({
                icon: "warning",
                title: "Conta em validação",
                text: "Aguarde a aprovação da sua conta para denunciar.",
                confirmButtonColor: "#449879",
            });
            return;
        }

        setIsModalOpen(true);
        setStep(1);
        setSelectedReason("");
        setDescription("");
    };

    const handleClose = () => {
        setIsModalOpen(false);
        setStep(1);
        setSelectedReason("");
        setDescription("");
    };

    const handleSubmitReport = async () => {
        if (!selectedReason) {
            Swal.fire({
                icon: "warning",
                title: "Selecione um motivo",
                text: "Por favor, selecione um motivo para a denúncia.",
                confirmButtonColor: "#449879",
            });
            return;
        }

        setLoading(true);

        try {
            const response = await api.post('/chat/reports', {
                conversation_id: conversationId,
                reason: selectedReason,
                description: description || "Sem descrição adicional.",
            });

            if (response.data.success) {
                handleClose();
                
                Swal.fire({
                    icon: "success",
                    title: "Denúncia enviada!",
                    text: "A equipe irá analisar a conversa.",
                    confirmButtonColor: "#449879",
                    timer: 3000,
                    showConfirmButton: false,
                });
            }
        } catch (error) {
            console.error("Error al enviar denúncia:", error);
            
            let errorMessage = "Não foi possível enviar a denúncia.";
            if (error.response?.data?.errors) {
                const errors = Object.values(error.response.data.errors).flat();
                errorMessage = errors.join('\n');
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            
            Swal.fire({
                icon: "error",
                title: "Erro",
                text: errorMessage,
                confirmButtonColor: "#449879",
            });
        } finally {
            setLoading(false);
        }
    };

    if (!user) return null;

    // 🔥 MODAL USANDO PORTAL (renderizado en document.body)
    const modalContent = isModalOpen ? (
        <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            style={{ 
                zIndex: 99999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                paddingTop: '80px',
                paddingBottom: '80px',
            }}
            onClick={handleClose}
        >
            <div 
                className="bg-white rounded-3xl shadow-2xl w-full"
                style={{ 
                    maxWidth: '440px',
                    maxHeight: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    animation: 'slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* HEADER */}
                <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="text-base font-bold text-gray-900">
                                Denunciar conversa
                            </h2>
                            <p className="text-xs text-gray-500 truncate">
                                com {otherUserName || "Usuário"}
                            </p>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-1.5 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                            aria-label="Fechar"
                        >
                            <X className="w-4 h-4 text-gray-400" />
                        </button>
                    </div>

                    {/* Indicador de pasos */}
                    <div className="flex items-center gap-2 mt-4">
                        <div className={`h-1 flex-1 rounded-full transition-colors ${step >= 1 ? 'bg-red-500' : 'bg-gray-200'}`}></div>
                        <div className={`h-1 flex-1 rounded-full transition-colors ${step >= 2 ? 'bg-red-500' : 'bg-gray-200'}`}></div>
                    </div>
                </div>

                {/* CONTENIDO */}
                <div className="px-6 py-5 overflow-y-auto flex-1">
                    {step === 1 ? (
                        <>
                            <p className="text-sm text-gray-600 mb-4">
                                Qual é o motivo da sua denúncia?
                            </p>

                            <div className="space-y-1.5">
                                {REPORT_REASONS.map((reason) => (
                                    <button
                                        key={reason.value}
                                        onClick={() => {
                                            setSelectedReason(reason.value);
                                            setStep(2);
                                        }}
                                        className={`w-full flex items-center justify-between p-3 rounded-xl text-left text-sm transition-all duration-200 ${
                                            selectedReason === reason.value
                                                ? 'bg-red-50 border-2 border-red-300 text-red-700'
                                                : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent text-gray-700'
                                        }`}
                                    >
                                        <span className="font-medium">{reason.label}</span>
                                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                    </button>
                                ))}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="mb-4 p-3 bg-red-50 rounded-xl border border-red-100">
                                <p className="text-xs text-red-600 font-medium mb-1">Motivo selecionado:</p>
                                <p className="text-sm text-red-800">
                                    {REPORT_REASONS.find(r => r.value === selectedReason)?.label}
                                </p>
                                <button
                                    onClick={() => setStep(1)}
                                    className="text-xs text-red-600 hover:underline mt-2"
                                >
                                    Alterar motivo
                                </button>
                            </div>

                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Detalhes adicionais <span className="text-gray-400">(opcional)</span>
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Descreva o que aconteceu para ajudar nossa equipe..."
                                rows="4"
                                maxLength={500}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 resize-none text-sm transition-all"
                            />
                            <p className="text-xs text-gray-400 mt-1 text-right">
                                {description.length}/500
                            </p>
                        </>
                    )}
                </div>

                {/* FOOTER (solo en paso 2) */}
                {step === 2 && (
                    <div className="px-6 py-4 bg-gray-50 flex gap-2 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => setStep(1)}
                            disabled={loading}
                            className="flex-1 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                        >
                            Voltar
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmitReport}
                            disabled={loading || !selectedReason}
                            className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm shadow-red-500/20"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Enviando...
                                </>
                            ) : (
                                <>
                                    <Flag className="w-4 h-4" />
                                    Enviar denúncia
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    ) : null;

    return (
        <>
            {/* 🔥 BOTÓN DE DENUNCIA EN EL HEADER */}
            <button
                onClick={handleOpenModal}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200"
                title="Denunciar conversa"
                aria-label="Denunciar conversa"
            >
                <Flag className="w-4 h-4" />
            </button>

            {/* 🔥 MODAL RENDERIZADO EN PORTAL (fuera del árbol del ChatDetail) */}
            {createPortal(modalContent, document.body)}

            {/* Estilos globales */}
            <style>{`
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px) scale(0.96);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
            `}</style>
        </>
    );
}